/**
 * SimulationCaseWizard — Tunnel de création de cas de simulation (4 étapes)
 * 1. Type (B2B/B2C + Sales/CC)  2. Offre  3. Persona(e)  4. Récapitulatif
 * Réutilise les composants, API et logiques existants — zéro nouvelle API/table.
 */

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { pickOfferLabel, pickOfferDescription } from "../lib/offer-i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, Check, X, Plus, Loader2, Sparkles, Pencil, Trash2, Building2, UserCircle, Package, Target } from "lucide-react";
import { useResizable } from "../hooks/useResizable";
import { ResizeHandles } from "./ui/resize-handles";
import { MultiSelect } from "./ui/multi-select";
import { accountApi, authApi } from "../lib/api";
import { AiChat } from "./AiChat";
import {
  PERSONA_FUNCTIONS, CSP_LIST, AGE_RANGES, PURCHASE_CRITERIA, PURCHASE_PHASES, PRODUCT_RELATIONSHIPS,
  CONTACT_REASONS, EXPRESSION_STYLES, MAIN_EXPECTATIONS, DECISION_MAKER_CODES, DECISION_MAKER_LABELS,
  PersonalityStyleSelect, MaturityLevelSelect, InternalPressureSelect, isPersonaComplete,
  personaNeedsPersonalityMigration, getPersonaLabel, getPersonaSubtitle, PersonaDetailView,
  pickDecisionMaker, PersonaForm,
} from "./PersonaUtils";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickPersonaField } from "../lib/persona-i18n";
import { COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS } from "../lib/company-options";
import { Badge } from "./ui/badge";

const STEPS = [
  { id: "simulator", labelKey: "wizard.simulationCase.stepSimulator" },
  { id: "type", labelKey: "wizard.simulationCase.stepType" },
  { id: "offer", labelKey: "wizard.simulationCase.stepOffer" },
  { id: "personae", labelKey: "wizard.simulationCase.stepPersonae" },
  { id: "context", labelKey: "wizard.simulationCase.stepContext" },
  { id: "recap", labelKey: "wizard.simulationCase.stepRecap" },
];

type ContextOption = {
  id: string;
  simulatorId: string;
  nameFr: string;
  nameEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  isActive: boolean;
};

type SimulatorOption = {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  simulatorType?: string | null;
  caseTypes?: string[] | null;
  color?: string | null;
  icon?: string | null;
};

// Valeur sentinelle de l'option "Autre" du MultiSelect Secteur d'activité.
// Quand présente dans le tableau `industry`, on affiche le champ texte libre.
// Préfixe __ pour éviter toute collision avec un vrai secteur.
const CUSTOM_INDUSTRY = "__autre_industry__";

interface SimulationCaseWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  accountId?: string;
  /**
   * Listes de l'étape « Type ». Optionnelles : à défaut, le tunnel prend le
   * référentiel partagé (`lib/company-options`) — c'est exactement ce que
   * passaient les appelants qui les fournissaient.
   *
   * Le défaut était auparavant un tableau VIDE, ce qui rendait le tunnel
   * inutilisable depuis tout écran qui oubliait ces props : taille d'entreprise
   * sans aucune option, secteur réduit à « Tous secteurs » et « Autre ». Les deux
   * champs étant obligatoires, « Suivant » restait grisé — cul-de-sac silencieux.
   * C'était le cas depuis les campagnes ET depuis l'assistant de parcours.
   */
  companySizeOptions?: string[];
  industryOptions?: string[];
  // Onboarding : ne proposer au step 0 que les simulateurs débloqués pour l'utilisateur
  // (ceux choisis à l'étape « simulateurs »). Défaut false → comportement « Mon compte » inchangé.
  restrictToUnlocked?: boolean;
  /**
   * Simulateurs mis en avant, hérités de la situation manager (« arrêter de
   * céder sur les prix » → Negotiator, ArguWin). Ils passent en tête, portent
   * un repère, et le seul recommandé est pré-sélectionné : le manager n'a plus
   * à choisir dans dix options à plat. Vide = comportement d'origine.
   */
  recommendedSimulators?: string[];
  // Assistant « compléter les cas » d'un parcours : le simulateur est imposé par la
  // phase → pré-sélectionné, l'étape 0 (choix simulateur) est sautée.
  presetSimulatorId?: string;
}

export function SimulationCaseWizard({ open, onClose, onCreated, accountId, companySizeOptions = COMPANY_SIZE_OPTIONS, industryOptions = INDUSTRY_OPTIONS, restrictToUnlocked = false, presetSimulatorId, recommendedSimulators }: SimulationCaseWizardProps) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 500, minHeight: 550,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 40 : 1100,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 40 : 850,
  });

  // --- State global ---
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  // Ref guard contre le double/triple clic rapide : `disabled={saving}` ne suffit pas car
  // entre setSaving(true) et le re-render du bouton, un clic répété peut quand même passer
  // dans handleCreate (incident démo 12/05/2026 : cas créé en triple, 3 targets identiques en DB).
  const creatingRef = useRef(false);

  // Étape 1 — Simulateur
  const [simulators, setSimulators] = useState<SimulatorOption[]>([]);
  const [selectedSimulator, setSelectedSimulator] = useState<SimulatorOption | null>(null);

  // Étape 2 — Type (caseType + champs B2B Sales)
  const [caseType, setCaseType] = useState<"B2B" | "B2C">("B2B");
  // category dérivée du simulateur (sales|customer_care). Conservée pour les personae.
  const category: "sales" | "customer_care" = (selectedSimulator?.category === "customer_care" ? "customer_care" : "sales");
  const [companySize, setCompanySize] = useState("");
  const [industry, setIndustry] = useState<string[]>([]);
  // Secteur "Autre" : quand l'option sentinelle CUSTOM_INDUSTRY est cochée dans
  // le MultiSelect, on affiche un champ texte libre (secteur niveau 2/3, ex
  // "Pharmacie"). La valeur saisie est injectée dans le tableau industry à la
  // soumission. Pattern inspiré du champ Fonction persona.
  const [customIndustry, setCustomIndustry] = useState("");

  // Étape 2 — Offre
  const [offerSubView, setOfferSubView] = useState<"choice" | "select" | "create" | "ai">("choice");
  const [existingOffers, setExistingOffers] = useState<any[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<{ id?: string; name: string; description: string } | null>(null);
  const [newOffer, setNewOffer] = useState({ name: "", description: "" });

  // Étape 3 — Personae
  const [personaSubView, setPersonaSubView] = useState<"list" | "choice" | "select" | "create" | "ai">("list");
  const [existingPersonae, setExistingPersonae] = useState<any[]>([]);
  const [personae, setPersonae] = useState<any[]>([]);
  const [newPersona, setNewPersona] = useState<Record<string, any>>({});
  const [editingPersonaIdx, setEditingPersonaIdx] = useState<number | null>(null);
  const [customFnMode, setCustomFnMode] = useState(false);
  // Persona dont on affiche le détail complet dans la modale (clic sur une carte).
  const [detailPersona, setDetailPersona] = useState<any | null>(null);

  // Étape 4 — Contexte de simulation (optionnel)
  const [contextsList, setContextsList] = useState<ContextOption[]>([]);
  const [selectedContextId, setSelectedContextId] = useState<string | null>(null);
  // Nom libre du cas — purement UI (étiquette de la carte côté Mon compte +
  // sélecteur de cas TrainingPage). Pas envoyé à N8N. Optionnel.
  const [displayName, setDisplayName] = useState("");

  // Contexte entreprise — companyLoaded permet à AiChat d'attendre la fin du
  // chargement avant d'envoyer son message d'init (sinon race condition :
  // l'agent répondrait "je n'ai pas accès à votre description").
  const [companyDescription, setCompanyDescription] = useState("");
  const [companyLoaded, setCompanyLoaded] = useState(false);

  // Chargement données
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSelectedSimulator(null);
    setCaseType("B2B");
    setCompanySize("");
    setIndustry([]);
    setCustomIndustry("");

    // Charger les simulateurs autorisés (training uniquement, exclut Sales Coach + Prep Meeting).
    // En onboarding (restrictToUnlocked), on ne garde que ceux débloqués pour l'utilisateur.
    // apiFetch (via authApi) : ajoute ?asUserId= en impersonation → l'accès (isUnlocked)
    // est calculé pour l'utilisateur ciblé, pas pour l'ADM.
    authApi.getAgents()
      .then((agents: any[]) => {
        // `let` : le tri « recommandés d'abord » ci-dessous réassigne la liste.
        let list: SimulatorOption[] = (agents || [])
          .filter((s: any) => s.simulatorType === "training" && (s.isActive ?? true) && (!restrictToUnlocked || (s.isUnlocked ?? true)));

        // Les recommandés d'abord. On compare par NOM : les environnements
        // n'ont pas les mêmes identifiants, et un nom absent du catalogue est
        // simplement ignoré plutôt que de vider la liste.
        const reco = new Set(recommendedSimulators ?? []);
        let onlyReco: SimulatorOption | null = null;
        if (reco.size > 0) {
          list = [...list].sort((a, b) => Number(reco.has(b.name)) - Number(reco.has(a.name)));
          const matches = list.filter((s) => reco.has(s.name));
          // Un seul recommandé : l'étape n'a plus d'objet.
          if (matches.length === 1) onlyReco = matches[0] ?? null;
        }
        setSimulators(list);
        // Simulateur imposé par une phase de parcours → pré-sélection + saut de l'étape 0.
        // Un simulateur imposé (assistant de parcours) ou un seul recommandé
        // (situation manager mono-simulateur) : même geste, l'étape est sautée.
        const preset = presetSimulatorId ? list.find((s) => s.id === presetSimulatorId) : onlyReco;
        if (preset) {
          setSelectedSimulator(preset);
          const types = Array.isArray(preset.caseTypes) && preset.caseTypes.length > 0 ? preset.caseTypes : ["B2B"];
          if (!types.includes(caseType)) setCaseType(types[0] as "B2B" | "B2C");
          setStep(1);
        }
      })
      .catch(() => setSimulators([]));
    setOfferSubView("choice");
    setSelectedOffer(null);
    setNewOffer({ name: "", description: "" });
    setPersonaSubView("list");
    setPersonae([]);
    setNewPersona({});
    setEditingPersonaIdx(null);
    setCustomFnMode(false);
    setDetailPersona(null);
    setSelectedContextId(null);

    setCompanyLoaded(false);
    accountApi.getCompany(accountId)
      .then((c: any) => setCompanyDescription(c?.description || ""))
      .catch(() => {})
      .finally(() => setCompanyLoaded(true));
    accountApi.getOffers(accountId).then((o: any) => setExistingOffers(o || [])).catch(() => {});
    accountApi.getContexts({}, accountId).then((list: any) => setContextsList(list || [])).catch(() => setContextsList([]));
    accountApi.getAccountPersonae(accountId).then((list: any) => setExistingPersonae((list || []).map((p: any) => ({
      ...p, // préserve les variantes i18n *Fr/*En
      id: p.id, personaType: p.personaType || p.persona_type || "B2B", category: p.category || "sales",
      function: p.function || "", decisionMakerProfile: p.decisionMakerProfile || p.decision_maker_profile || "",
      painPoints: p.painPoints || p.pain_points || "", csp: p.csp || "", ageRange: p.ageRange || p.age_range || "",
      purchaseCriteria: p.purchaseCriteria || p.purchase_criteria || "", purchasePhase: p.purchasePhase || p.purchase_phase || "",
      productRelationship: p.productRelationship || p.product_relationship || "",
      contactReason: p.contactReason || p.contact_reason || "", expressionStyle: p.expressionStyle || p.expression_style || "",
      mainExpectation: p.mainExpectation || p.main_expectation || "",
    })))).catch(() => {});
  }, [open]);

  // Reset du contexte si on change de simulateur (le contexte est lié à un simulateur)
  useEffect(() => {
    if (!selectedContextId) return;
    const ctx = contextsList.find((c) => c.id === selectedContextId);
    if (!ctx || ctx.simulatorId !== selectedSimulator?.id) {
      setSelectedContextId(null);
    }
  }, [selectedSimulator, contextsList, selectedContextId]);

  if (!open) return null;

  // --- Validation par étape ---
  const canNext = () => {
    if (step === 0) return !!selectedSimulator;
    if (step === 1) {
      // B2B Sales : taille d'entreprise + secteur d'activité obligatoires.
      // (B2C et B2B Customer Care ne montrent pas ces champs → pas d'impact.)
      if (caseType === "B2B" && category === "sales") {
        const hasIndustry =
          industry.some((v) => v !== CUSTOM_INDUSTRY) ||
          (industry.includes(CUSTOM_INDUSTRY) && customIndustry.trim() !== "");
        return !!companySize && hasIndustry;
      }
      return true; // type — caseType déjà initialisé
    }
    if (step === 2) return !!selectedOffer;
    if (step === 3) return personae.length > 0;
    if (step === 4) return true; // contexte optionnel
    return true;
  };

  // --- Création finale ---
  const handleCreate = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setSaving(true);
    try {
      const offerIds: string[] = [];

      // Créer l'offre si elle est nouvelle (pas d'id)
      if (selectedOffer && !selectedOffer.id) {
        const created = await accountApi.createOffer({ name: selectedOffer.name, description: selectedOffer.description }, accountId);
        offerIds.push(created.id);
      } else if (selectedOffer?.id) {
        offerIds.push(selectedOffer.id);
      }

      // Construit le tableau final des secteurs : on remplace la sentinelle
      // "Autre" par la valeur libre saisie (customIndustry). Si "Autre" est
      // coché mais le champ vide, on l'ignore simplement.
      const industryFinal = industry
        .filter((v) => v !== CUSTOM_INDUSTRY)
        .concat(industry.includes(CUSTOM_INDUSTRY) && customIndustry.trim() ? [customIndustry.trim()] : []);

      // Créer le target (lié au simulateur sélectionné, contexte optionnel)
      const target = await accountApi.createTarget({
        caseType,
        companySize: caseType === "B2B" ? companySize : undefined,
        industry: industryFinal.length > 0 ? JSON.stringify(industryFinal) : undefined,
        offerIds,
        simulatorId: selectedSimulator?.id,
        contextId: selectedContextId || undefined,
        displayName: displayName.trim() || undefined,
      }, accountId);

      // Rattacher les personae au cas (relation M<->N — plus de duplication)
      for (const p of personae) {
        if (p.id) {
          // Persona existante du référentiel → on la LIE au cas (sans la cloner).
          await accountApi.linkPersonaToTarget(target.id, p.id, accountId);
        } else {
          // Persona nouvelle saisie dans le wizard → création (+ lien posé côté API).
          await accountApi.createPersona(target.id, { ...p, personaType: caseType, category }, accountId);
        }
      }

      onCreated();
      onClose();
    } catch (err: any) {
      alert(err.message || t("wizard.simulationCase.createError"));
    } finally {
      setSaving(false);
      creatingRef.current = false;
    }
  };

  // --- Persona : initialiser un nouveau avec les bons défauts ---
  const initNewPersona = () => ({
    personaType: caseType,
    category,
    function: "",
    decisionMakerProfile: "",
    painPoints: "",
    csp: "",
    ageRange: "",
    purchaseCriteria: "",
    purchasePhase: "",
    productRelationship: "",
    contactReason: "",
    expressionStyle: "",
    mainExpectation: "",
    personalMotivations: "",
    dailyIrritants: "",
    typicalObjections: "",
    neverDisclose: "",
    personalityStyle: "",
    maturityLevel: "",
    internalPressure: "",
  });


  // --- Rendu principal ---
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true"
      onClick={() => { if (!document.body.style.cursor) onClose(); }}>
      <div ref={containerRef} onClick={(e) => e.stopPropagation()} data-tour="case-wizard"
        className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={boxStyle(680, 720)}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b shrink-0" style={{ backgroundColor: "#A6D3F9" + "20" }}>
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              {t("wizard.simulationCase.title")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t("wizard.simulationCase.stepIndicator", { current: step + 1, total: STEPS.length, label: t(STEPS[step].labelKey) })}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1 px-6 py-2 shrink-0">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 h-1.5 rounded-full transition-colors"
              style={{ backgroundColor: i <= step ? "#A6D3F9" : "#E5E7EB" }} />
          ))}
        </div>

        {/* Contenu (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ===== ÉTAPE 1 : Simulateur ===== */}
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("wizard.simulationCase.simulatorIntro")}
              </p>
              {simulators.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">{t("wizard.simulationCase.loading")}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {simulators.map((s) => {
                    const isSelected = selectedSimulator?.id === s.id;
                    const types = Array.isArray(s.caseTypes) && s.caseTypes.length > 0 ? s.caseTypes : ["B2B"];
                    const cat = s.category === "customer_care" ? t("wizard.simulationCase.catCustomerCare") : t("wizard.simulationCase.catSales");
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          setSelectedSimulator(s);
                          // Auto-aligner caseType si une seule valeur supportée
                          if (types.length === 1) setCaseType(types[0] as "B2B" | "B2C");
                          else if (!types.includes(caseType)) setCaseType(types[0] as "B2B" | "B2C");
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected ? "border-[#384872] bg-[#384872]/5" : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {(() => {
                            const photo = s.image || (typeof s.icon === "string" && (s.icon.startsWith("data:") || s.icon.startsWith("http")) ? s.icon : null);
                            if (photo) {
                              return <img src={photo} alt="" className="h-10 w-10 rounded-md object-cover flex-shrink-0" />;
                            }
                            if (s.icon) {
                              return <div className="h-10 w-10 rounded-md flex-shrink-0 flex items-center justify-center text-xl" style={{ background: s.color || "#A6D3F9" }}>{s.icon}</div>;
                            }
                            return <div className="h-10 w-10 rounded-md flex-shrink-0" style={{ background: s.color || "#A6D3F9" }} />;
                          })()}
                          <div className="flex-1 min-w-0">
                            <p className="flex flex-wrap items-center gap-1.5 font-semibold text-sm" style={{ color: "#384872" }}>
                              {s.name}
                              {(recommendedSimulators ?? []).includes(s.name) && (
                                <span
                                  className="rounded-full px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide"
                                  style={{ background: "#E6F3EB", color: "#1E7A3C" }}
                                >
                                  {t("wizard.simulationCase.recommended")}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">{cat}</p>
                            <div className="flex gap-1 mt-1.5 flex-wrap">
                              {types.map((t) => (
                                <span key={t} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ===== ÉTAPE 2 : Type (B2B/B2C) + champs Sales B2B ===== */}
          {step === 1 && selectedSimulator && (
            <div className="space-y-6">
              {(() => {
                const supportedTypes = Array.isArray(selectedSimulator.caseTypes) && selectedSimulator.caseTypes.length > 0 ? selectedSimulator.caseTypes : ["B2B"];
                const onlyOne = supportedTypes.length === 1;
                return (
                  <>
                    <div>
                      <Label className="text-sm font-semibold mb-3 block">{t("wizard.simulationCase.marketType")}</Label>
                      {onlyOne ? (
                        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-[#384872]">
                          {t("wizard.simulationCase.onlyOneTypeSupported", { simulator: selectedSimulator.name, type: supportedTypes[0] })}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {(["B2B", "B2C"] as const).map((ct) => {
                            const supported = supportedTypes.includes(ct);
                            return (
                              <button
                                key={ct}
                                disabled={!supported}
                                onClick={() => supported && setCaseType(ct)}
                                className={`p-4 rounded-xl border-2 transition-all text-center ${
                                  !supported
                                    ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                                    : caseType === ct
                                    ? "border-[#384872] bg-[#384872]/5"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <Building2 className={`h-6 w-6 mx-auto mb-2 ${caseType === ct && supported ? "text-[#384872]" : "text-gray-400"}`} />
                                <p className="font-semibold text-sm">{ct}</p>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  {ct === "B2B" ? t("wizard.simulationCase.b2bDescription") : t("wizard.simulationCase.b2cDescription")}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* B2B Sales : taille + secteur */}
                    {caseType === "B2B" && category === "sales" && (
                      <div className="space-y-3 pt-2 border-t">
                        <Label className="text-sm font-semibold">{t("wizard.simulationCase.targetDetails")}</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">{t("wizard.simulationCase.companySize")} <span className="text-[#C0423F]">*</span></Label>
                            <Select value={companySize} onValueChange={setCompanySize}>
                              <SelectTrigger><SelectValue placeholder={t("wizard.simulationCase.select")} /></SelectTrigger>
                              <SelectContent>
                                {companySizeOptions.map((opt) => <SelectItem key={opt} value={opt}>{localizeCompanySize(opt, i18n.language)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">{t("wizard.simulationCase.industry")} <span className="text-[#C0423F]">*</span></Label>
                            <MultiSelect
                              getLabel={(opt) => opt === CUSTOM_INDUSTRY ? t("wizard.simulationCase.industryOther") : localizeIndustry(opt, i18n.language)}
                              options={[t("wizard.simulationCase.allIndustries"), ...industryOptions, CUSTOM_INDUSTRY]}
                              value={industry}
                              onChange={(values) => {
                                const allLabel = t("wizard.simulationCase.allIndustries");
                                if (values.includes(allLabel) && !industry.includes(allLabel)) {
                                  setIndustry([allLabel]);
                                } else if (values.includes(allLabel) && values.length > 1) {
                                  setIndustry(values.filter((v) => v !== allLabel));
                                } else {
                                  setIndustry(values);
                                }
                              }}
                              placeholder={t("wizard.simulationCase.select")}
                            />
                            {/* Champ texte libre affiché quand "Autre" est sélectionné */}
                            {industry.includes(CUSTOM_INDUSTRY) && (
                              <Input
                                value={customIndustry}
                                onChange={(e) => setCustomIndustry(e.target.value)}
                                placeholder={t("wizard.simulationCase.industryOtherPlaceholder")}
                                className="mt-1.5"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ===== ÉTAPE 2 : Offre ===== */}
          {step === 2 && (
            <div className="space-y-3">
              {/* Offre déjà sélectionnée */}
              {selectedOffer && offerSubView === "choice" && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-green-50/50 mb-3">
                  <Package className="h-4 w-4 text-[#1E7A3C] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{selectedOffer.name}</p>
                    {selectedOffer.description && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap break-words">{selectedOffer.description}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setNewOffer({ name: selectedOffer.name, description: selectedOffer.description }); setOfferSubView("create"); }}><Pencil className="h-3 w-3 text-gray-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedOffer(null)}><X className="h-3 w-3 text-red-400" /></Button>
                </div>
              )}

              {/* Choix initial */}
              {!selectedOffer && offerSubView === "choice" && (
                <>
                  <p className="text-sm text-muted-foreground">{t("wizard.simulationCase.offerIntro")}</p>
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <button onClick={() => setOfferSubView("select")}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                      <Package className="h-6 w-6" style={{ color: "#7CBF94" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.offerSelectExisting")}</span>
                    </button>
                    <button onClick={() => setOfferSubView("ai")}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                      <Sparkles className="h-6 w-6" style={{ color: "#7CBF94" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.offerCreateWithAi")}</span>
                    </button>
                    <button onClick={() => { setNewOffer({ name: "", description: "" }); setOfferSubView("create"); }}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                      <Plus className="h-6 w-6" style={{ color: "#7CBF94" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.offerCreateManually")}</span>
                    </button>
                  </div>
                </>
              )}

              {/* Sélection offre existante */}
              {offerSubView === "select" && (
                <div className="space-y-2">
                  <button onClick={() => setOfferSubView("choice")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <p className="text-sm font-medium">{t("wizard.simulationCase.offerSelectTitle")}</p>
                  {existingOffers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{t("wizard.simulationCase.offerNoneExisting")}</p>
                  ) : (
                    <div className="max-h-[500px] overflow-y-auto space-y-1">
                      {existingOffers.map((o: any) => (
                        <button key={o.id} onClick={() => { setSelectedOffer({ id: o.id, name: pickOfferLabel(o, i18n.language), description: pickOfferDescription(o, i18n.language) || "" }); setOfferSubView("choice"); }}
                          className="w-full text-left p-3 rounded-lg border hover:border-[#7CBF94] hover:bg-green-50 transition-all">
                          {(() => {
                            const dispName = pickOfferLabel(o, i18n.language);
                            const dispDesc = pickOfferDescription(o, i18n.language);
                            return (
                              <>
                                <p className="text-sm font-medium">{dispName}</p>
                                {dispDesc && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap break-words">{dispDesc}</p>}
                              </>
                            );
                          })()}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Création manuelle */}
              {offerSubView === "create" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">{t("wizard.simulationCase.offerNameLabel")}</Label>
                    <Input value={newOffer.name} onChange={(e) => setNewOffer({ ...newOffer, name: e.target.value })} placeholder={t("wizard.simulationCase.offerNamePlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("wizard.simulationCase.offerDescription")}</Label>
                    <Textarea value={newOffer.description} onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })} rows={3} />
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <Button size="sm" variant="outline" onClick={() => setOfferSubView("choice")}>
                      <X className="h-3 w-3 mr-1" />{t("common.cancel")}
                    </Button>
                    <Button size="sm" disabled={!newOffer.name.trim()} onClick={() => { setSelectedOffer({ name: newOffer.name.trim(), description: newOffer.description.trim() }); setOfferSubView("choice"); }}
                      className="bg-[#7CBF94] hover:bg-[#7CBF94]/90 text-white">
                      <Check className="h-4 w-4 mr-1" />{t("wizard.simulationCase.offerValidate")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Création avec IA */}
              {offerSubView === "ai" && (
                <div className="space-y-2">
                  <button onClick={() => setOfferSubView("choice")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div style={{ height: "min(70vh, 600px)", minHeight: "400px" }}>
                    <AiChat
                      agentCode="offer_creator" robotColor="#7CBF94" bgColor="#f7faf8"
                      context={{ companyDescription, language: i18n.language }}
                      ready={companyLoaded}
                      onItemCreated={(_type, data) => { setSelectedOffer({ name: data.name, description: data.description || "" }); setOfferSubView("choice"); }}
                      onComplete={() => setOfferSubView("choice")}
                      title={t("wizard.simulationCase.offerAiTitle")}
                      subtitle={t("wizard.simulationCase.offerAiSubtitle")}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== ÉTAPE 3 : Personae ===== */}
          {step === 3 && (
            <div className="space-y-3">
              {/* Liste des personae ajoutés */}
              {personaSubView === "list" && (
                <>
                  <p className="text-sm text-muted-foreground">
                    {personae.length === 0
                      ? t("wizard.simulationCase.personaeEmpty")
                      : t("wizard.simulationCase.personaeCount", { count: personae.length })}
                  </p>
                  {personae.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-lg border bg-purple-50/50">
                      <UserCircle className="h-4 w-4 text-[#5A4BBE] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{getPersonaLabel(p) || t("wizard.simulationCase.personaFallbackName")}</p>
                        {caseType === "B2B" && p.decisionMakerProfile && <p className="text-[10px] text-muted-foreground">{pickDecisionMaker(p)}</p>}
                        {caseType === "B2B" && category === "sales" && p.painPoints && <p className="text-[10px] text-muted-foreground truncate">{pickPersonaField(p, "painPoints", i18n.language)}</p>}
                        {caseType === "B2C" && category === "sales" && p.ageRange && <p className="text-[10px] text-muted-foreground">{p.ageRange}</p>}
                        {caseType === "B2C" && category === "sales" && p.purchaseCriteria && <p className="text-[10px] text-muted-foreground">{localizePersonaOption(p.purchaseCriteria, i18n.language)}</p>}
                        {caseType === "B2C" && category === "sales" && p.productRelationship && <p className="text-[10px] text-muted-foreground">{localizePersonaOption(p.productRelationship, i18n.language)}</p>}
                        {caseType === "B2C" && category === "sales" && p.purchasePhase && <p className="text-[10px] text-muted-foreground">{localizePersonaOption(p.purchasePhase, i18n.language)}</p>}
                        {category === "customer_care" && p.expressionStyle && <p className="text-[10px] text-muted-foreground">{localizePersonaOption(p.expressionStyle, i18n.language)}</p>}
                        {category === "customer_care" && p.mainExpectation && <p className="text-[10px] text-muted-foreground">{localizePersonaOption(p.mainExpectation, i18n.language)}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setNewPersona({ ...p }); setEditingPersonaIdx(i); setCustomFnMode(p.function ? !PERSONA_FUNCTIONS.includes(p.function) : false); setPersonaSubView("create"); }}><Pencil className="h-3 w-3 text-gray-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setPersonae(personae.filter((_, j) => j !== i))}><X className="h-3 w-3 text-gray-400" /></Button>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <button onClick={() => { setNewPersona(initNewPersona()); setEditingPersonaIdx(null); setCustomFnMode(false); setPersonaSubView("select"); }}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <UserCircle className="h-6 w-6" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.personaSelectExisting")}</span>
                    </button>
                    <button onClick={() => setPersonaSubView("ai")}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <Sparkles className="h-6 w-6" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.personaCreateWithAi")}</span>
                    </button>
                    <button onClick={() => { setNewPersona(initNewPersona()); setEditingPersonaIdx(null); setCustomFnMode(false); setPersonaSubView("create"); }}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <Plus className="h-6 w-6" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("wizard.simulationCase.personaCreateManually")}</span>
                    </button>
                  </div>
                </>
              )}

              {/* Sélection persona existant — cartes compactes + case à cocher (multi-select).
                  Clic sur le corps de la carte → modale détail complète (helpers PersonaUtils). */}
              {personaSubView === "select" && (() => {
                const filtered = existingPersonae.filter((p: any) => p.personaType === caseType && p.category === category);
                const selectedIds = new Set(personae.filter((p: any) => p.id).map((p: any) => p.id));
                const toggle = (p: any) => {
                  if (selectedIds.has(p.id)) {
                    setPersonae(personae.filter((x: any) => x.id !== p.id));
                  } else {
                    setPersonae([...personae, { ...p }]);
                  }
                };
                return (
                <div className="space-y-2">
                  {/* Pas de lien « retour » ici : la barre du bas porte Retour et Suivant,
                      comme à chaque étape. Deux retours à deux endroits égaraient. */}
                  <p className="text-sm font-medium">{t(isFr ? "wizard.simulationCase.personaSelectTitleFr" : "wizard.simulationCase.personaSelectTitleEn", { caseType, category: isFr ? (category === "sales" ? t("wizard.simulationCase.personaCategorySales") : t("wizard.simulationCase.personaCategoryCustomerCare")) : category })}</p>
                  {filtered.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{t("wizard.simulationCase.personaNoneAvailable")}</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">{t("wizard.simulationCase.personaSelectHint")}</p>
                      {/* Même libellé que la vue liste : cocher se lit tout de suite. */}
                      {personae.length > 0 && (
                        <p className="text-xs font-medium text-[#5A4BBE]">{t("wizard.simulationCase.personaeCount", { count: personae.length })}</p>
                      )}
                      <div className="max-h-[440px] overflow-y-auto space-y-1.5">
                        {filtered.map((p: any) => {
                          const checked = selectedIds.has(p.id);
                          return (
                            <div
                              key={p.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${checked ? "border-[#988EF2] bg-purple-50/60" : "border-gray-200 hover:border-[#988EF2]/60"}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(p)}
                                aria-label={getPersonaLabel(p)}
                                className="h-4 w-4 shrink-0 accent-[#988EF2] cursor-pointer"
                              />
                              <button
                                type="button"
                                onClick={() => setDetailPersona(p)}
                                className="flex-1 min-w-0 text-left"
                              >
                                <p className="text-sm font-medium truncate">{getPersonaLabel(p)}</p>
                                {getPersonaSubtitle(p) && <p className="text-xs text-muted-foreground truncate">{getPersonaSubtitle(p)}</p>}
                              </button>
                              <button
                                type="button"
                                onClick={() => setDetailPersona(p)}
                                className="text-[11px] font-medium text-[#5A4BBE] hover:underline shrink-0"
                              >
                                {t("wizard.simulationCase.personaViewDetails")}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {/* Sortie UNIQUE de cet écran (le lien « Retour » du haut a été retiré).
                      Le clic sur une carte sert à cocher/décocher (multi-select) : il ne peut donc
                      pas valider ET revenir comme le fait le choix d'une offre (sélection unique).
                      Ce bouton ramène à la vue « list », seule vue qui affiche le pied de page
                      « Suivant ». Il reste TOUJOURS cliquable — grisé à 0 coché, il enfermerait
                      l'utilisateur — et se présente en « Annuler » tant que rien n'est sélectionné.
                      Rendu hors du ternaire filtered.length : sinon aucune sortie quand la liste
                      est vide. */}
                  <div className="flex items-center justify-end pt-2">
                    <Button size="sm" onClick={() => setPersonaSubView("list")}
                      variant={selectedIds.size === 0 ? "outline" : "default"}
                      className={selectedIds.size === 0 ? undefined : "bg-[#988EF2] hover:bg-[#988EF2]/90 text-white"}>
                      {selectedIds.size === 0
                        ? <><X className="h-4 w-4 mr-1" />{t("common.cancel")}</>
                        : <><Check className="h-4 w-4 mr-1" />{t("wizard.simulationCase.personaValidateSelection", { count: selectedIds.size })}</>}
                    </Button>
                  </div>
                </div>
                );
              })()}

              {/* Création manuelle — formulaire persona UNIQUE partagé (PersonaUtils) */}
              {personaSubView === "create" && (
                <div className="space-y-3">
                  <PersonaForm
                    persona={newPersona}
                    caseType={caseType as "B2B" | "B2C" | undefined}
                    category={category as "sales" | "customer_care" | undefined}
                    onSave={(form) => {
                      if (editingPersonaIdx !== null) {
                        const upd = [...personae]; upd[editingPersonaIdx] = { ...form }; setPersonae(upd);
                      } else {
                        setPersonae([...personae, { ...form }]);
                      }
                      setEditingPersonaIdx(null);
                      setPersonaSubView("list");
                    }}
                    onCancel={() => { setPersonaSubView("list"); setEditingPersonaIdx(null); }}
                  />
                </div>
              )}

              {/* Création avec IA */}
              {personaSubView === "ai" && (
                <div className="space-y-2">
                  <button onClick={() => setPersonaSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div style={{ height: "min(70vh, 600px)", minHeight: "400px" }}>
                    <AiChat
                      agentCode="persona_creator" robotColor="#988EF2" bgColor="#f8f5ff"
                      context={{ companyDescription, offers: selectedOffer ? `${selectedOffer.name}: ${selectedOffer.description}` : "", language: i18n.language }}
                      ready={companyLoaded}
                      onItemCreated={(_type, data) => { setPersonae([...personae, { ...data, personaType: caseType, category }]); setPersonaSubView("list"); }}
                      onComplete={() => setPersonaSubView("list")}
                      title={t("wizard.simulationCase.personaAiTitle")}
                      subtitle={t("wizard.simulationCase.personaAiSubtitle")}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== ÉTAPE 4 : Contexte de simulation (optionnel) ===== */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    <strong>{t("wizard.simulationCase.contextWarningTitle")}</strong>{" "}
                    {t("wizard.simulationCase.contextWarningBody")}
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                {t("wizard.simulationCase.contextIntro")}
              </p>

              {(() => {
                const availableContexts = contextsList
                  .filter((c) => c.simulatorId === selectedSimulator?.id && c.isActive)
                  .sort((a, b) => (isFr ? a.nameFr : a.nameEn).localeCompare(isFr ? b.nameFr : b.nameEn));

                if (availableContexts.length === 0) {
                  return (
                    <div className="text-center py-8 rounded-lg border border-dashed border-gray-300 bg-gray-50/50">
                      <Target className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {t("wizard.simulationCase.contextNoneForSimulator", { simulator: selectedSimulator?.name || t("wizard.simulationCase.contextThisSimulator") })}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("wizard.simulationCase.contextCanContinueWithout")}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {/* Option "Aucun contexte" */}
                    <button
                      type="button"
                      onClick={() => setSelectedContextId(null)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        selectedContextId === null
                          ? "border-[#384872] bg-[#384872]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <p className="text-sm font-medium" style={{ color: "#384872" }}>
                        {t("wizard.simulationCase.contextNoSpecific")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t("wizard.simulationCase.contextNoSpecificDescription")}
                      </p>
                    </button>

                    {availableContexts.map((c) => {
                      const isSelected = selectedContextId === c.id;
                      const name = isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr);
                      const desc = isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setSelectedContextId(c.id)}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? "border-[#384872] bg-[#384872]/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <p className="text-sm font-medium" style={{ color: "#384872" }}>{name}</p>
                          {desc && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{desc}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== ÉTAPE 5 : Récapitulatif ===== */}
          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("wizard.simulationCase.recapIntro")}</p>

              {/* Nom libre du cas — purement UI, optionnel. Placé tout en haut du
                  récapitulatif pour que l'user nomme son cas en connaissance de cause. */}
              <div className="p-4 rounded-lg border bg-blue-50/30 space-y-1.5">
                <Label className="text-sm font-semibold">
                  {isFr ? "Nom du cas (optionnel)" : "Case name (optional)"}
                </Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={isFr ? "Ex: Cold call PME chimie — relance budget" : "Ex: Cold call SMB chemistry — budget follow-up"}
                  maxLength={120}
                />
                <p className="text-xs text-muted-foreground">
                  {isFr
                    ? "Sert juste à vous y retrouver dans la liste. Si vide, la taille d'entreprise sert d'étiquette."
                    : "Just a UI label. If empty, the company size is used as label."}
                </p>
              </div>

              {/* Type */}
              <div className="p-4 rounded-lg border bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-[#384872]" />
                    <span className="text-sm font-semibold">{t("wizard.simulationCase.recapTypeLabel")}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setStep(1)}><Pencil className="h-3 w-3" /></Button>
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <span className="text-xs px-2 py-1 rounded-full bg-[#384872]/10 text-[#384872] font-medium">{caseType}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-[#384872]/10 text-[#384872] font-medium">{category === "sales" ? t("wizard.simulationCase.recapSales") : t("wizard.simulationCase.recapCustomerCare")}</span>
                  {caseType === "B2B" && companySize && <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{localizeCompanySize(companySize, i18n.language)}</span>}
                  {industry.length > 0 && <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{industry.map((v) => v === CUSTOM_INDUSTRY ? (customIndustry.trim() || t("wizard.simulationCase.industryOther")) : localizeIndustry(v, i18n.language)).join(", ")}</span>}
                </div>
              </div>

              {/* Offre */}
              <div className="p-4 rounded-lg border bg-green-50/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-[#1E7A3C]" />
                    <span className="text-sm font-semibold">{t("wizard.simulationCase.recapOfferLabel")}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setStep(2); setOfferSubView("choice"); }}><Pencil className="h-3 w-3" /></Button>
                </div>
                {selectedOffer && (
                  <div className="mt-2">
                    <p className="text-sm font-medium">{selectedOffer.name}</p>
                    {selectedOffer.description && <p className="text-xs text-muted-foreground mt-0.5">{selectedOffer.description.substring(0, 150)}{selectedOffer.description.length > 150 ? "…" : ""}</p>}
                  </div>
                )}
              </div>

              {/* Personae */}
              <div className="p-4 rounded-lg border bg-purple-50/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCircle className="h-4 w-4 text-[#5A4BBE]" />
                    <span className="text-sm font-semibold">{t("wizard.simulationCase.recapPersonaeLabel", { count: personae.length })}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setStep(3); setPersonaSubView("list"); }}><Pencil className="h-3 w-3" /></Button>
                </div>
                <div className="mt-2 space-y-1">
                  {personae.map((p, i) => (
                    <div key={i} className="p-2 rounded border bg-purple-50/30">
                      <p className="text-sm font-medium">{getPersonaLabel(p) || t("wizard.simulationCase.personaFallbackName")}</p>
                      {caseType === "B2B" && p.decisionMakerProfile && <p className="text-xs text-muted-foreground">{pickDecisionMaker(p)}</p>}
                      {caseType === "B2B" && category === "sales" && p.painPoints && <p className="text-xs text-muted-foreground truncate">{pickPersonaField(p, "painPoints", i18n.language)}</p>}
                      {caseType === "B2C" && category === "sales" && p.ageRange && <p className="text-xs text-muted-foreground">{p.ageRange}</p>}
                      {caseType === "B2C" && category === "sales" && p.purchaseCriteria && <p className="text-xs text-muted-foreground">{localizePersonaOption(p.purchaseCriteria, i18n.language)}</p>}
                      {caseType === "B2C" && category === "sales" && p.productRelationship && <p className="text-xs text-muted-foreground">{localizePersonaOption(p.productRelationship, i18n.language)}</p>}
                      {category === "customer_care" && p.expressionStyle && <p className="text-xs text-muted-foreground">{localizePersonaOption(p.expressionStyle, i18n.language)}</p>}
                      {category === "customer_care" && p.mainExpectation && <p className="text-xs text-muted-foreground">{localizePersonaOption(p.mainExpectation, i18n.language)}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Contexte */}
              <div className="p-4 rounded-lg border bg-amber-50/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#926B00]" />
                    <span className="text-sm font-semibold">{t("wizard.simulationCase.recapContextLabel")}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setStep(4)}><Pencil className="h-3 w-3" /></Button>
                </div>
                <div className="mt-2">
                  {(() => {
                    const ctx = contextsList.find((c) => c.id === selectedContextId);
                    if (!ctx) {
                      return <p className="text-xs text-muted-foreground italic">{t("wizard.simulationCase.contextNoSpecific")}</p>;
                    }
                    const name = isFr ? (ctx.nameFr || ctx.nameEn) : (ctx.nameEn || ctx.nameFr);
                    const desc = isFr ? (ctx.descriptionFr || ctx.descriptionEn) : (ctx.descriptionEn || ctx.descriptionFr);
                    return (
                      <>
                        <p className="text-sm font-medium">{name}</p>
                        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation — masqué en mode édition/création inline (formulaire
            manuel, assistant IA), qui portent leurs propres boutons de validation.
            La SÉLECTION de personae, elle, garde la barre : les cases cochées
            s'appliquent en direct, l'utilisateur doit pouvoir continuer d'ici. */}
        {(step === 0 || step === 1 || (step === 2 && offerSubView === "choice") || (step === 3 && (personaSubView === "list" || personaSubView === "select")) || step === 4 || step === 5) && (
        <div className="flex items-center justify-between px-6 py-3 border-t shrink-0 bg-gray-50/50">
          {(() => {
            // Avec un simulateur imposé (assistant parcours), l'étape 1 est le plancher :
            // « retour » ferme au lieu de revenir au choix du simulateur (étape 0 sautée).
            const atFloor = step === 0 || (!!presetSimulatorId && step === 1);
            // Depuis la sélection de personae, « retour » ramène à la liste du cas —
            // pas à l'étape précédente, qu'on n'a pas quittée.
            const inPersonaSelect = step === 3 && personaSubView === "select";
            return (
              <Button
                variant="outline"
                onClick={() => {
                  if (inPersonaSelect) return setPersonaSubView("list");
                  return atFloor ? onClose() : setStep(step - 1);
                }}
                size="sm"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {atFloor ? t("common.cancel") : t("common.back")}
              </Button>
            );
          })()}

          {step < STEPS.length - 1 ? (
            // On repasse la sous-vue persona sur « list » en avançant : un retour
            // arrière retombe ainsi sur les personae du cas, pas sur le sélecteur.
            <Button onClick={() => { setPersonaSubView("list"); setStep(step + 1); }} disabled={!canNext()} size="sm"
              style={{ backgroundColor: "#384872" }}>
              {t("wizard.simulationCase.next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={saving || !canNext()} size="sm"
              className="bg-[#1E7A3C] hover:bg-[#1E7A3C]/90 text-white">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              {t("wizard.simulationCase.createSimulationCase")}
            </Button>
          )}
        </div>
        )}

        <ResizeHandles onPointerDown={onPointerDown} />
      </div>

      {/* Modale détail persona — totalité des infos structurées avec titres de champs,
          dans la langue courante. Réutilise PersonaDetailView (helpers PersonaUtils +
          persona-i18n) pour couvrir les 4 combos + sections Réalisme/Psychologie. */}
      {detailPersona && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDetailPersona(null)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden w-full max-w-md max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b shrink-0" style={{ backgroundColor: "#988EF2" + "20" }}>
              <div className="min-w-0">
                <h3 className="font-bold text-base truncate" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                  {getPersonaLabel(detailPersona)}
                </h3>
                <p className="text-xs text-muted-foreground">{t("wizard.simulationCase.personaDetailsTitle")}</p>
              </div>
              <button onClick={() => setDetailPersona(null)} className="text-muted-foreground hover:text-foreground shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <PersonaDetailView persona={detailPersona} isFr={isFr} />
            </div>
            <div className="flex justify-end px-5 py-3 border-t shrink-0 bg-gray-50/50">
              <Button size="sm" variant="outline" onClick={() => setDetailPersona(null)}>
                {t("common.close")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
